/**
 * MovementsModal — Exibe movimentações + invoices de um title_debt.
 * Reutilizado em TitleDebtPageList e OperationTitleDebtsPage.
 */
import { useEffect, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import BadgeStatus from '../../../atoms/BadgeStatus';
import { statusColors, movementTypeLabels, settlementTypeLabels } from '../constants/settlement';
import { getTitleDebtMovements } from '../services/settlementApi';

interface Props {
    titleDebt: any;
    onClose: () => void;
}

const formatCurrency = (value: string | number) => {
    const num = typeof value === 'string' ? parseFloat(value || '0') : value;
    return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatDate = (date: string | null) => {
    if (!date) return '-';
    const [y, m, d] = date.split('-');
    return `${d}/${m}/${y}`;
};

const MovementsModal: React.FC<Props> = ({ titleDebt, onClose }) => {
    const [movements, setMovements] = useState<any[]>([]);
    const [invoices, setInvoices] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const data = await getTitleDebtMovements(titleDebt.id);
                setMovements(data.data || []);
                setInvoices(data.invoices || []);
            } catch { setMovements([]); setInvoices([]); }
            setLoading(false);
        })();
    }, [titleDebt.id]);

    const remaining = parseFloat(titleDebt.amount) - parseFloat(titleDebt.paid_amount || '0');

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Movimentações — {titleDebt.contract_number} (Parcela {titleDebt.installment_number || titleDebt.document_title || '-'})
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                        <FaTimes size={18} />
                    </button>
                </div>

                {/* Summary bar */}
                <div className="mb-4 grid grid-cols-6 gap-4 text-sm bg-gray-50 dark:bg-slate-700 p-3 rounded">
                    <div><span className="text-gray-500">Valor:</span> <strong>{formatCurrency(titleDebt.amount)}</strong></div>
                    <div><span className="text-gray-500">Pago:</span> <strong className="text-green-600">{formatCurrency(titleDebt.paid_amount || '0')}</strong></div>
                    <div><span className="text-gray-500">Saldo:</span> <strong className="text-red-600">{formatCurrency(remaining)}</strong></div>
                    <div><span className="text-gray-500">Vencimento:</span> <strong>{formatDate(titleDebt.due_date)}</strong></div>
                    <div><span className="text-gray-500">Status:</span> <BadgeStatus text={titleDebt.status} color={statusColors[titleDebt.status] || 'gray'} /></div>
                    <div><span className="text-gray-500">Modalidade:</span> <strong>{titleDebt.contract_modality || '-'}</strong></div>
                </div>

                {loading ? (
                    <p className="text-center text-gray-500 py-4">Carregando...</p>
                ) : movements.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">Nenhuma movimentação encontrada</p>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100 dark:bg-slate-700">
                            <tr className="text-left text-xs text-gray-500 dark:text-gray-400 uppercase">
                                <th className="px-3 py-2">Tipo</th>
                                <th className="px-3 py-2 text-right">Valor</th>
                                <th className="px-3 py-2">Data</th>
                                <th className="px-3 py-2">Fonte</th>
                                <th className="px-3 py-2">Liquidação</th>
                                <th className="px-3 py-2">Conta</th>
                                <th className="px-3 py-2">Nosso Nº</th>
                                <th className="px-3 py-2 text-right">Juros</th>
                                <th className="px-3 py-2 text-right">Custas</th>
                                <th className="px-3 py-2">Obs</th>
                            </tr>
                        </thead>
                        <tbody>
                            {movements.map((m: any) => (
                                <tr key={m.id} className="border-t dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700">
                                    <td className="px-3 py-2">
                                        <span className={m.movement_type === 'REVERSAL' ? 'text-red-600' : ''}>
                                            {movementTypeLabels[m.movement_type] || m.movement_type}
                                            {m.invoice?.is_subrogation && <span className="ml-1 text-xs bg-purple-100 text-purple-700 px-1 rounded">Sub</span>}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-right font-medium">{formatCurrency(m.amount)}</td>
                                    <td className="px-3 py-2">{formatDate(m.movement_date)}</td>
                                    <td className="px-3 py-2">{m.payment_source || '-'}</td>
                                    <td className="px-3 py-2">{settlementTypeLabels[m.settlement_type] || '-'}</td>
                                    <td className="px-3 py-2 text-xs">{m.invoice?.bank_account || '-'}</td>
                                    <td className="px-3 py-2 text-xs font-mono">{m.invoice?.our_number || '-'}</td>
                                    <td className="px-3 py-2 text-right">{parseFloat(m.interest_amount || '0') > 0 ? formatCurrency(m.interest_amount) : '-'}</td>
                                    <td className="px-3 py-2 text-right">{parseFloat(m.court_costs || '0') > 0 ? formatCurrency(m.court_costs) : '-'}</td>
                                    <td className="px-3 py-2 text-xs text-gray-600 max-w-[150px] truncate" title={m.invoice?.notes || m.notes || ''}>
                                        {m.invoice?.notes || m.notes || '-'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                {invoices.length > 0 && (
                    <div className="mt-6">
                        <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Boletos / Invoices</h3>
                        <table className="w-full text-sm">
                            <thead className="bg-blue-50 dark:bg-slate-700">
                                <tr className="text-left text-xs text-gray-500 uppercase">
                                    <th className="px-3 py-2 text-right">Valor</th>
                                    <th className="px-3 py-2">Vencimento</th>
                                    <th className="px-3 py-2">Status</th>
                                    <th className="px-3 py-2">Conta</th>
                                    <th className="px-3 py-2">Nosso Nº</th>
                                    <th className="px-3 py-2">Meio</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoices.map((inv: any) => (
                                    <tr key={inv.id} className="border-t dark:border-slate-600">
                                        <td className="px-3 py-2 text-right font-medium">{formatCurrency(inv.amount)}</td>
                                        <td className="px-3 py-2">{formatDate(inv.due_date)}</td>
                                        <td className="px-3 py-2"><BadgeStatus text={inv.status || 'OPEN'} color={statusColors[inv.status] || 'yellow'} /></td>
                                        <td className="px-3 py-2 text-xs">{inv.bank_account || '-'}</td>
                                        <td className="px-3 py-2 text-xs font-mono">{inv.our_number || '-'}</td>
                                        <td className="px-3 py-2 text-xs">{inv.payment_method || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MovementsModal;
